import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType, Result } from "@zxing/library";
import { isValidEan13 } from "../../utils/book-barcode";

interface Props {
  onScan: (barcode: string) => void;
  onClose: () => void;
  manualFallback?: boolean;
}

const BookBarcodeScannerDialog = ({ onScan, onClose, manualFallback = false }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls>();
  const [error, setError] = useState("");
  const [entered, setEntered] = useState(false);
  const [manualBarcode, setManualBarcode] = useState("");
  const [manualError, setManualError] = useState("");

  const stopScanner = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = undefined;
    const stream = videoRef.current?.srcObject;
    if (typeof MediaStream !== "undefined" && stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  useEffect(() => {
    if (!entered) return;
    const video = videoRef.current;
    if (!video) return;
    const hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13]);
    const reader = new BrowserMultiFormatReader(hints);
    let active = true;
    let accepted = false;

    void reader.decodeFromConstraints(
      { video: { facingMode: { ideal: "environment" } }, audio: false },
      video,
      (result: Result | undefined, _error, controls) => {
        if (!active || accepted || !result) return;
        const barcode = result.getText();
        if (!isValidEan13(barcode)) return;
        accepted = true;
        controls?.stop();
        onScan(barcode);
      },
    ).then((controls) => {
      if (!active) controls.stop();
      else controlsRef.current = controls;
    }).catch(() => {
      if (active) setError("카메라를 사용할 수 없습니다. 바코드를 직접 입력해 주세요.");
    });

    return () => {
      active = false;
      stopScanner();
    };
  }, [entered, onScan, stopScanner]);

  const applyManualBarcode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const barcode = manualBarcode.trim();
    if (!isValidEan13(barcode)) {
      setManualError("바코드 13자리와 체크숫자를 확인해 주세요.");
      return;
    }
    setManualError("");
    stopScanner();
    onScan(barcode);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" TransitionProps={{ onEntered: () => setEntered(true) }}>
      <DialogTitle>책 바코드 스캔</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">책 뒷면의 바코드가 화면 중앙에 오도록 비춰주세요.</Typography>
          <Box
            component="video"
            ref={videoRef}
            muted
            playsInline
            aria-label="도서 등록 바코드 카메라"
            sx={{ width: "100%", minHeight: 220, bgcolor: "grey.900", borderRadius: 1, objectFit: "cover" }}
          />
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {error && manualFallback ? (
            <Box component="form" onSubmit={applyManualBarcode} noValidate>
              <Stack spacing={1}>
                <TextField
                  label="바코드 직접 입력"
                  value={manualBarcode}
                  onChange={(event) => {
                    setManualBarcode(event.target.value.replace(/\D/g, "").slice(0, 13));
                    if (manualError) setManualError("");
                  }}
                  inputProps={{ inputMode: "numeric", maxLength: 13 }}
                  error={Boolean(manualError)}
                  helperText={manualError || "ISBN/EAN-13 숫자 13자리를 입력해 주세요."}
                />
                <Button type="submit" variant="contained">바코드 적용</Button>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookBarcodeScannerDialog;
